import {User} from "auth/types";
import {isRoleAtLeast} from "utils/roles";
import pages, {Page, PageGroup} from "./pages";
import {ComponentType, ReactNode} from "react";

export type NavPage = {
  title: string;
  segment: string;
  icon: ReactNode;
  country?: string;
};

export type NavPageGroup = {
  title: string;
  segment: string;
  icon: ReactNode;
  children: Array<NavPage | NavPageGroup>;
};

export type NavItem = NavPage | NavPageGroup;

/**
 * Type guard for Page (not PageGroup)
 */
const isPage = (node: Page | PageGroup): node is Page => {
  return (node as Page).Component !== undefined;
};

/**
 * Type guard for PageGroup nodes that have children.
 * Returns true if the node is a PageGroup and its children property is a non-empty array.
 */
export const isPageGroupWithChildren = (
  node: Page | PageGroup,
): node is PageGroup & {children: Array<Page | PageGroup>} => {
  return (
    "children" in node &&
    Array.isArray((node as PageGroup).children) &&
    (node as PageGroup).children.length > 0
  );
};

/**
 * Helper to convert an Icon component type to a JSX element.
 */
const iconToElement = (IconOrElement: ComponentType<any>): ReactNode => {
  if (typeof IconOrElement === "function" || typeof IconOrElement === "object")
    return <IconOrElement />;
};

/**
 * Returns the filtered navigation tree for the given user using the page array.
 * Omits PageGroups with no children.
 * If a PageGroup has only one child after filtering, returns the child with the title "PageGroupTitle/ChildTitle".
 * Also converts Icon: ComponentType => icon: JSX.Element for UI usage.
 */
export const filterPages = (
  items: Array<Page | PageGroup>,
  user: User,
): Array<NavItem> => {
  return items
    .filter(item =>
      isPage(item)
        ? isRoleAtLeast(user.role, item.minRole) &&
          (!item.country || item.country === user.country)
        : true,
    )
    .flatMap(item => {
      if (isPageGroupWithChildren(item)) {
        const filteredChildren = filterPages(item.children, user);

        if (filteredChildren.length === 0) return [];

        if (filteredChildren.length === 1) {
          const child = filteredChildren[0];
          return [
            {
              ...child,
              title: `${item.title} - ${child.title}`,
              segment: `${item.segment}/${child.segment}`,
              icon: iconToElement(item.Icon),
            },
          ];
        }

        // Return as NavPageGroup for UI
        return [
          {
            title: item.title,
            segment: item.segment,
            icon: iconToElement(item.Icon),
            children: filteredChildren,
          },
        ];
      }

      // Return as NavPage for UI
      return [
        {
          title: item.title,
          segment: item.segment,
          icon: iconToElement(item.Icon),
          country: item.country,
        },
      ];
    });
};
/**
 * Returns the first accessible navigation path for the given user.
 * Used for resolving the default landing page.
 */
export const getDefaultLandingPath = (user: User): string | null => {
  const findFirstLeaf = (
    items: Array<Page | PageGroup>,
    path: string[] = [],
  ): string | null => {
    for (const item of items) {
      // Only check role/country for Pages
      const allowed = isPage(item)
        ? isRoleAtLeast(user.role, item.minRole) &&
          (!item.country || item.country === user.country)
        : true;

      if (allowed) {
        const currentPath = [...path, item.segment];
        if (isPageGroupWithChildren(item)) {
          const childPath = findFirstLeaf(item.children, currentPath);
          if (childPath) return childPath;
        } else if (isPage(item)) {
          // Leaf node (has a Component)
          return currentPath.join("/");
        }
      }
    }
    return null;
  };

  return findFirstLeaf(pages);
};

/**
 * Validates if a given path segment is accessible to the user based on their role and country.
 * Supports both direct segments and nested paths (e.g., "notifications/send-sms").
 */
export const isPathAccessibleToUser = (
  user: User,
  pathSegment: string,
): boolean => {
  const checkPath = (
    items: Array<Page | PageGroup>,
    remainingPath: string,
  ): boolean => {
    const [currentSegment, ...rest] = remainingPath.split("/");

    for (const item of items) {
      if (item.segment === currentSegment) {
        // Only check role/country for Pages
        const isRoleAllowed = isPage(item)
          ? isRoleAtLeast(user.role, item.minRole)
          : true;
        const isCountryAllowed = isPage(item)
          ? !item.country || item.country === user.country
          : true;

        if (!isRoleAllowed || !isCountryAllowed) {
          return false;
        }

        // If this is the last segment in the path, we found a valid match
        if (rest.length === 0) {
          return true;
        }

        // If there are more segments, check children
        if (isPageGroupWithChildren(item)) {
          return checkPath(item.children, rest.join("/"));
        }

        // No children but more segments means invalid path
        return false;
      }
    }

    return false;
  };

  return checkPath(pages, pathSegment);
};

/**
 * Extracts page titles from navigation data for a given path.
 * Returns the title for the exact path match, or null if not found.
 * Supports nested paths (e.g., "notifications/send-sms").
 */
export const getPageTitle = (pathSegment: string): string | null => {
  const findTitle = (
    items: Array<Page | PageGroup>,
    remainingPath: string,
  ): string | null => {
    const [currentSegment, ...rest] = remainingPath.split("/");

    for (const item of items) {
      if (item.segment === currentSegment) {
        // If this is the last segment in the path, we found a valid match
        if (rest.length === 0) {
          return item.title;
        }

        // If there are more segments, check children
        if (isPageGroupWithChildren(item)) {
          return findTitle(item.children, rest.join("/"));
        }

        // No children but more segments means invalid path
        return null;
      }
    }

    return null;
  };

  return findTitle(pages, pathSegment);
};

/**
 * Returns a filtered navigation menu for the given user.
 * - Respects role hierarchy (via `isRoleAtLeast`)
 * - Optionally restricts items by country
 * - Supports nested children
 * - Flattens PageGroups with only a single child
 * - Converts Icon to icon (JSX.Element) for UI
 */
const getNavigation = (user: User): Array<NavItem> => {
  return filterPages(pages, user);
};

export default getNavigation;
